import { isChunkLoadError } from "./is-chunk-load-error.ts"
import {
  reloadForChunkError,
  scheduleChunkReloadReset
} from "./reload-for-chunk-error.ts"

let recoveringImports = 0

// A screen opened through lazyPage retries its own file before giving up.
// Vite reports the same failure as `vite:preloadError` before the import
// rejects, so while such a load is in flight that event is left alone and the
// rejection reaches the screen's own recovery, which reloads only as a last
// resort. One failure must never cost two reloads of the budget.
export const whileRecoveringImport = async <T>(
  load: () => Promise<T>
): Promise<T> => {
  recoveringImports += 1
  try {
    return await load()
  } finally {
    recoveringImports -= 1
  }
}

// Every other import keeps the old behaviour: a stale file after a deploy
// reloads the app rather than showing a crash.
export const installChunkErrorHandlers = (target: Window = window) => {
  const onPreloadError = (event: Event) => {
    if (recoveringImports > 0) return
    event.preventDefault()
    reloadForChunkError((event as Event & { payload?: unknown }).payload)
  }
  const onError = (event: ErrorEvent) => {
    if (!isChunkLoadError(event)) return
    event.preventDefault()
    reloadForChunkError(event.error ?? event)
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return
    event.preventDefault()
    reloadForChunkError(event.reason)
  }

  target.addEventListener("vite:preloadError", onPreloadError)
  target.addEventListener("error", onError)
  target.addEventListener("unhandledrejection", onRejection)
  scheduleChunkReloadReset()

  return () => {
    target.removeEventListener("vite:preloadError", onPreloadError)
    target.removeEventListener("error", onError)
    target.removeEventListener("unhandledrejection", onRejection)
  }
}
